import { useEffect, useState } from 'react';
import Modal from '../Modal';
import 'react-day-picker/dist/style.css';
import { DayPicker } from 'react-day-picker';
import HoursInput from './HoursInput.jsx';
import {
    dateStringToDateTime,
    formatDate,
    formatDateLong,
    formatInternalDate,
    printHoursSet,
} from '../../lib/DateTime.js';
import {
    setSpecificHoursQuery,
    useSpecificHoursQuery,
} from '../../lib/Queries';
import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import Loading from '../Loading';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Toggle from '~/components/Toggle.jsx';

const AddSpecificHoursModal = ({
    isOpen,
    onClose,
    doSubmitAsync,
    initialHours,
    initialDate,
}) => {
    const defaultHours = [{ start: '09:00', end: '17:00' }];
    const [selectedDates, setSelectedDates] = useState([]);
    const [hours, setHours] = useState(defaultHours);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Reset values
            if (initialHours) setHours(initialHours);
            if (initialDate) setSelectedDates([initialDate]);
        } else {
            // Reset after the animation hiding the modal is done
            setTimeout(() => {
                setSelectedDates([]);
                setHours(defaultHours);
            }, 300);
        }
    }, [isOpen]);

    async function save() {
        if (selectedDates.length === 0) {
            return;
        }

        setIsSaving(true);
        await doSubmitAsync(selectedDates, hours);
        setIsSaving(false);
        onClose();
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} widthClass="sm:max-w-[400px]">
            <div className="">
                <div className="">
                    <DayPicker
                        className="!mx-0"
                        mode="multiple"
                        selected={selectedDates}
                        onSelect={setSelectedDates}
                        classNames={{
                            months: 'flex-none',
                            table: 'w-full',
                        }}
                    />
                </div>
                <div className="">
                    <div className="flex items-center justify-end my-2 text-sm text-gray-500">
                        Not available
                        <Toggle
                            enabled={hours.length === 0}
                            setEnabled={enabled => {
                                if (enabled) {
                                    setHours([]);
                                } else {
                                    setHours(defaultHours);
                                }
                            }}
                            className="ml-2"
                        />
                    </div>

                    <div className="input-label">
                        Apply hours to
                    </div>
                    {selectedDates.length > 0 && (
                        <div className="text-sm mb-2">
                            {selectedDates.map(date => formatDate(date)).join(', ')}
                        </div>
                    )}
                    {selectedDates.length === 0 && (
                        <div className="text-red-600 mb-2">Select date(s) above</div>
                    )}
                    {hours.length > 0 && (
                        <HoursInput hours={hours} setHours={setHours} />
                    )}

                    {hours.length === 0 && selectedDates.length > 0 && (
                        <div className="leading-tight">
                            You are closed{' '}
                            {selectedDates.length > 1
                                ? 'these dates'
                                : 'this day'}
                            , no availability will be shown.
                        </div>
                    )}

                    <button
                        type="button"
                        className="btn-save mt-4"
                        onClick={save}
                    >
                        {isSaving ? 'Saving...' : 'Save Availability'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

const SpecificHours = ({ resource, availability, availabilityGroup }) => {
    const [isModalShowing, setIsModalShowing] = useState(false);
    const [id, setId] = useState();
    const [modalHours, setModalHours] = useState([]);
    const [modalDate, setModalDate] = useState();

    const { data, isLoading } = useSpecificHoursQuery(
        resource.id,
        availabilityGroup,
    );
    const specificHours = data || [];

    const { save, isSaving } = useBusinessPostApi(
        `/scheduling/availability/${resource.id}/specific-hours`,
    );

    function showModal(id, hours, date) {
        setId(id);
        setModalDate(date);
        setModalHours(hours);
        setIsModalShowing(true);
    }

    async function onSubmit(dates, hours) {
        await save({
            dates: dates.map(d => formatInternalDate(d)),
            hours: hours,
            id: id,
            availabilityGroup: availabilityGroup,
        }).then(data =>
            setSpecificHoursQuery(resource.id, availabilityGroup, data),
        );
    }

    function deleteHours(id) {
        if (confirm('Are you sure you want to delete this?')) {
            save({
                dates: [],
                hours: [],
                id: id,
                availabilityGroup: availabilityGroup,
            }).then(data =>
                setSpecificHoursQuery(resource.id, availabilityGroup, data),
            );
        }
    }

    return (
        <div>
            <button
                type="button"
                className="btn-light-pill mt-2"
                onClick={() => showModal(0)}
            >
                Add date specific availability
            </button>

            <AddSpecificHoursModal
                isOpen={isModalShowing}
                onClose={() => setIsModalShowing(false)}
                doSubmitAsync={onSubmit}
                initialHours={modalHours}
                initialDate={modalDate}
            />
            <div>
                {isLoading && <Loading />}
                {specificHours.map(specific => (
                    <div
                        key={specific.id}
                        className="py-3 flex justify-between"
                    >
                        <div className="flex flex-1 items-start">
                            <div className="w-48 mr-2">
                                {specific.internal_date &&
                                    formatDateLong(
                                        dateStringToDateTime(
                                            specific.internal_date,
                                        ),
                                        resource.timezone,
                                    )}
                            </div>
                            <div className="flex-1">
                                {specific.hours.map((hoursSet, idx) => (
                                    <div key={idx}>
                                        {!!hoursSet?.end &&
                                            !!hoursSet?.start &&
                                            printHoursSet(hoursSet)}
                                    </div>
                                ))}
                                {specific.hours.length === 0 && (
                                    <div>Closed</div>
                                )}
                            </div>
                        </div>
                        <div>
                            <button
                                type="button"
                                onClick={() => deleteHours(specific.id)}
                            >
                                <XMarkIcon className="text-red-600 w-5 h-5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SpecificHours;
